/**
 * Checkpoints — reading and writing the definition of a station.
 *
 * The only reader of this collection. A partial one used to live in
 * `members.ts` for the location chooser, returning just id, name and number;
 * two readers of one collection is how they drift, so that one is gone and
 * `ChooseLocation` uses this.
 *
 * Rules already permit what this needs: `read` for any member of the event,
 * `write` for an organiser. Nothing in `firestore.rules` changes for Stage A's
 * checkpoint CRUD — but `tests/rules/checkpoints.rules.test.ts` is new, because
 * the collection had no coverage at all and is about to be written through.
 */

import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  updateDoc,
} from 'firebase/firestore';

import type { Checkpoint, CheckpointId, Facilities } from '@cairn/domain';

import { getServices } from './firebase.js';

/** Everything except the id, which is the document path rather than a field. */
export type CheckpointEdits = Omit<Checkpoint, 'id'>;

const NO_FACILITIES: Facilities = {
  bed: false,
  water: false,
  food: false,
  medic: false,
  mechanical: false,
  bagDrop: false,
  dryRoom: false,
  toilets: false,
};

/**
 * A stored document is `unknown` until proven otherwise, and a checkpoint
 * written before a field existed is a normal thing to meet. Absent values take
 * the safe reading rather than throwing: no facilities, no location, and a
 * dwell limit that inherits the event default.
 */
function toCheckpoint(id: string, data: Record<string, unknown>): Checkpoint {
  const facilities = (data['facilities'] ?? {}) as Partial<Facilities>;
  const location = data['location'] as Record<string, unknown> | null | undefined;

  return {
    id: id as CheckpointId,
    number: (data['number'] as number | undefined) ?? 0,
    name: (data['name'] as string | undefined) ?? '',
    role: (data['role'] as Checkpoint['role'] | undefined) ?? 'intermediate',
    routeOrder: (data['routeOrder'] as number | undefined) ?? 0,
    physicalSiteId: (data['physicalSiteId'] as string | undefined) ?? '',
    // null is meaningful — it inherits the event default — so `?? null` is
    // correct here and a `|| null` would turn a deliberate 0 into inheritance.
    maxDwellMinutes: (data['maxDwellMinutes'] as number | null | undefined) ?? null,
    staffingModel: (data['staffingModel'] as Checkpoint['staffingModel'] | undefined) ?? 'staffed',
    isSleepStop: (data['isSleepStop'] as boolean | undefined) ?? false,
    facilities: { ...NO_FACILITIES, ...facilities },
    location:
      location === null || location === undefined
        ? null
        : {
            what3words: (location['what3words'] as string | null | undefined) ?? null,
            latitude: (location['latitude'] as number | null | undefined) ?? null,
            longitude: (location['longitude'] as number | null | undefined) ?? null,
          },
    connectivity: (data['connectivity'] as Checkpoint['connectivity'] | undefined) ?? 'cell',
  };
}

/** Plain data for Firestore. Domain types never reach the SDK, and vice versa. */
function toDocument(edits: CheckpointEdits): Record<string, unknown> {
  return {
    number: edits.number,
    name: edits.name,
    role: edits.role,
    routeOrder: edits.routeOrder,
    physicalSiteId: edits.physicalSiteId,
    maxDwellMinutes: edits.maxDwellMinutes,
    staffingModel: edits.staffingModel,
    isSleepStop: edits.isSleepStop,
    facilities: { ...edits.facilities },
    location: edits.location === null ? null : { ...edits.location },
    connectivity: edits.connectivity,
  };
}

/**
 * Ordered along the route, never by number.
 *
 * `routeOrder` is what auto-close and ETA depend on, and a figure-of-8 course
 * reuses a site under a different number — so sorting by number would put the
 * course in the wrong order in exactly the case the field exists for.
 */
export async function listCheckpoints(eventId: string): Promise<readonly Checkpoint[]> {
  const { db } = await getServices();

  const snapshot = await getDocs(
    query(collection(db, 'events', eventId, 'checkpoints'), orderBy('routeOrder')),
  );

  return snapshot.docs.map((d) => toCheckpoint(d.id, d.data()));
}

export async function watchCheckpoints(
  eventId: string,
  onChange: (checkpoints: readonly Checkpoint[]) => void,
): Promise<() => void> {
  const { db } = await getServices();

  return onSnapshot(
    query(collection(db, 'events', eventId, 'checkpoints'), orderBy('routeOrder')),
    (snapshot) => {
      onChange(snapshot.docs.map((d) => toCheckpoint(d.id, d.data())));
    },
  );
}

export async function getCheckpoint(
  eventId: string,
  checkpointId: string,
): Promise<Checkpoint | null> {
  const { db } = await getServices();
  const snapshot = await getDoc(doc(db, 'events', eventId, 'checkpoints', checkpointId));
  return snapshot.exists() ? toCheckpoint(snapshot.id, snapshot.data()) : null;
}

/**
 * Create, refusing to overwrite.
 *
 * `setDoc` would silently replace an existing checkpoint, and on a
 * configuration document that means losing a colleague's work with no trace.
 * The transaction makes the existence check and the write atomic, so two
 * organisers creating the same id at once cannot both believe they succeeded.
 */
export async function createCheckpoint(eventId: string, checkpoint: Checkpoint): Promise<void> {
  const { db } = await getServices();
  const ref = doc(db, 'events', eventId, 'checkpoints', checkpoint.id);

  await runTransaction(db, async (tx) => {
    const existing = await tx.get(ref);
    if (existing.exists()) {
      throw new Error(`A checkpoint with the id ${checkpoint.id} already exists.`);
    }
    const { id: _id, ...edits } = checkpoint;
    tx.set(ref, toDocument(edits));
  });
}

/**
 * Update, refusing to create. `updateDoc` fails on a missing document, which is
 * the behaviour wanted: editing a checkpoint someone else has just deleted
 * should say so rather than quietly resurrect it.
 */
export async function updateCheckpoint(
  eventId: string,
  checkpointId: string,
  edits: CheckpointEdits,
): Promise<void> {
  const { db } = await getServices();
  await updateDoc(doc(db, 'events', eventId, 'checkpoints', checkpointId), toDocument(edits));
}

/**
 * Delete a checkpoint.
 *
 * Safe while an event is being configured, and **not** safe once it has run:
 * passages and log entries reference a checkpoint by id, and rule 5 says
 * nothing is ever hard-deleted because current state is a projection of an
 * append-only log. Deleting a checkpoint that participants have passed through
 * would leave those references dangling.
 *
 * Stage B should replace this with an archive flag once passages exist. Until
 * then there is nothing to orphan, and the alternative — no way to remove a
 * checkpoint added by mistake — is worse.
 */
export async function deleteCheckpoint(eventId: string, checkpointId: string): Promise<void> {
  const { db } = await getServices();
  await deleteDoc(doc(db, 'events', eventId, 'checkpoints', checkpointId));
}
